import { API } from "aws-amplify"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"

const CORE = "CORE"

// ---------------------------------------------------------------------------
// Fetch primitives — Amplify injects Cognito Bearer token automatically.
// ---------------------------------------------------------------------------

const fetchSeries = async (organizationId, serieType) => {
  if (!organizationId) return []
  const params = new URLSearchParams()
  if (serieType) params.set("serie_type", serieType)
  // Always fetch archived too; the UI filters in-memory (toggle "Ver archivadas").
  params.set("include_archived", "true")
  const url = `/series/${organizationId}?${params.toString()}`
  return API.get(CORE, url)
}

const fetchCreateSerie = async ({ organizationId, serieType, body }) => {
  const url = `/series/${organizationId}?serie_type=${serieType}`
  return API.post(CORE, url, { body })
}

const fetchUpdateSerie = async ({ organizationId, serieSK, body }) => {
  const url = `/series/${organizationId}/${encodeURIComponent(serieSK)}`
  return API.patch(CORE, url, { body })
}

const fetchArchiveSerie = async ({ organizationId, serieSK }) => {
  const url = `/series/${organizationId}/${encodeURIComponent(serieSK)}/archive`
  return API.post(CORE, url)
}

const fetchSetDefaultSerie = async ({ organizationId, serieSK }) => {
  const url = `/series/${organizationId}/${encodeURIComponent(serieSK)}/set-default`
  return API.post(CORE, url)
}

// ---------------------------------------------------------------------------
// React Query hooks
// ---------------------------------------------------------------------------

export const seriesQueryKey = (organizationId, serieType) => [
  "series",
  organizationId,
  serieType,
]

/**
 * Loads ALL series for a (org, serieType) pair, including archived.
 * Cache keeps the data per (org, serieType) so switching tabs and toggling
 * "Ver archivadas" never re-fires the request.
 */
export const useSeries = (
  organizationId,
  serieType,
  { enabled = true } = {},
) => {
  return useQuery({
    queryKey: seriesQueryKey(organizationId, serieType),
    queryFn: () => fetchSeries(organizationId, serieType),
    enabled: !!organizationId && !!serieType && enabled,
    staleTime: 5 * 60 * 1000, // 5 min — series rarely change in this UI
  })
}

const useInvalidateSeries = (organizationId, serieType) => {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({
      queryKey: seriesQueryKey(organizationId, serieType),
    })
}

const optimisticUpdate = (queryClient, key, updater) => {
  const previous = queryClient.getQueryData(key)
  if (Array.isArray(previous)) {
    queryClient.setQueryData(key, previous.map(updater))
  }
  return previous
}

export const useCreateSerie = (organizationId, serieType) => {
  const invalidate = useInvalidateSeries(organizationId, serieType)
  return useMutation({
    mutationFn: body => fetchCreateSerie({ organizationId, serieType, body }),
    onSuccess: invalidate,
  })
}

export const useUpdateSerie = (organizationId, serieType) => {
  const invalidate = useInvalidateSeries(organizationId, serieType)
  return useMutation({
    mutationFn: ({ serieSK, body }) =>
      fetchUpdateSerie({ organizationId, serieSK, body }),
    onSuccess: invalidate,
  })
}

export const useArchiveSerie = (organizationId, serieType) => {
  const queryClient = useQueryClient()
  const key = seriesQueryKey(organizationId, serieType)
  return useMutation({
    mutationFn: serieSK => fetchArchiveSerie({ organizationId, serieSK }),
    onMutate: async serieSK => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = optimisticUpdate(queryClient, key, s =>
        s.SK === serieSK ? { ...s, archived: true } : s,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(key, context.previous)
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  })
}

export const useSetDefaultSerie = (organizationId, serieType) => {
  const queryClient = useQueryClient()
  const key = seriesQueryKey(organizationId, serieType)
  return useMutation({
    mutationFn: serieSK => fetchSetDefaultSerie({ organizationId, serieSK }),
    onMutate: async serieSK => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = optimisticUpdate(queryClient, key, s => {
        if (s.SK === serieSK) return { ...s, is_default: true }
        if (s.is_default) return { ...s, is_default: false }
        return s
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(key, context.previous)
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  })
}
